import { useEffect, useState } from "react"

import { App, Flex, Modal, Spin, Typography } from "antd"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"

import Icon from "../../../../Icon"
import { usersActions } from "../../redux/users.slice"
import { getCreditAutomationAccess, setCreditAutomationAccess } from "../../services"

const { Text } = Typography

// Enable / Disable Credit Automation for a user (CHA1-3565).
// Enable is a plain confirmation; Disable first fetches the access state to learn how many
// approved system credit requests without attachments are still open — while that count is > 0
// the backend refuses to turn it off, so the Disable button stays disabled.
export const CreditAutomationModal = ({ record, mode, onClose }) => {
  const { t } = useTranslation()
  const { notification } = App.useApp()
  const dispatch = useDispatch()

  const isEnable = mode === "enable"
  const userName = record?.user_full_name || record?.user_email || "user"

  const [fetching, setFetching] = useState(false)
  const [access, setAccess] = useState(null)
  const [submitting, setSubmitting] = useState(false)

  const notificationConfig = {
    closeIcon: <Text>{t("Dismiss")}</Text>,
    style: { minWidth: "max-content", width: 475 }
  }

  useEffect(() => {
    if (isEnable) return
    let active = true
    setFetching(true)
    getCreditAutomationAccess({ user: record?.id }).then((res) => {
      if (!active) return
      if (res.ok) {
        setAccess(res.data)
      } else {
        notification.error({
          message: res.data?.message?.detail || "Failed to load credit automation state.",
          ...notificationConfig
        })
      }
      setFetching(false)
    })
    return () => {
      active = false
    }
  }, [])

  const openRequests = access?.open_requests_count || 0
  const blockDisable = !isEnable && openRequests > 0

  const handleOk = () => {
    setSubmitting(true)
    return setCreditAutomationAccess({ user: record?.id, is_granted: isEnable }).then((res) => {
      setSubmitting(false)
      if (res.ok) {
        dispatch(
          usersActions.setCreditAutomationAccess({ user: record?.id, has_access: isEnable })
        )
        notification.success({
          message: `Credit Automation has been turned ${isEnable ? "on" : "off"} for ${userName}`,
          placement: "bottomRight",
          ...notificationConfig
        })
        onClose()
      } else {
        notification.error({
          message: res.data?.message?.detail || "Failed to update credit automation.",
          ...notificationConfig
        })
      }
    })
  }

  return (
    <Modal
      open
      centered
      width={480}
      classNames={{ content: "clear-bg" }}
      title={isEnable ? "Enable Credit Automation?" : "Disable Credit Automation?"}
      okText={isEnable ? "Enable" : "Disable"}
      cancelText="Cancel"
      confirmLoading={submitting}
      okButtonProps={{ size: "large", disabled: fetching || blockDisable }}
      cancelButtonProps={{ size: "large" }}
      onOk={handleOk}
      onCancel={onClose}
    >
      {isEnable ? (
        <Flex vertical gap={16}>
          <Text type="secondary">
            The Automatic Credits feature will become available to this user, allowing them to
            submit the automation request for their business accounts.
          </Text>
          <Text type="secondary">
            Users will stop processing system credit requests and providing the necessary evidence
          </Text>
        </Flex>
      ) : (
        <Spin spinning={fetching}>
          <Flex vertical gap={16}>
            <Text type="secondary">
              The Automatic Credits feature will stop working for this user.
            </Text>
            <Flex
              gap={8}
              align="flex-start"
              style={{ background: "#FFF4E5", borderRadius: 8, padding: 12 }}
            >
              <Icon
                type="alert_triangle_error"
                style={{ fontSize: 18, flexShrink: 0, marginTop: 2, color: "#E8833A" }}
              />
              <Text>
                Filter Approved system credits without attachments on the Request→Credits page and
                manually change the status to Requires proof.
              </Text>
            </Flex>
            {blockDisable && (
              <Text type="danger">
                {`Move ${openRequests} approved system credit requests without attachments to Requires proof before switching Credit Automation off.`}
              </Text>
            )}
            <Text type="secondary">
              Users will start processing system credit requests and providing the necessary
              evidence.
            </Text>
          </Flex>
        </Spin>
      )}
    </Modal>
  )
}
